import { useCallback, useMemo, useState } from 'react';
import { isClientSide } from '@r1-frontend/shared/helpers/ssr';
import defaultImageFallback from '~/public/defaultImageFallback.svg';

interface ITimeout {
    hasTimeout?: boolean;
    timeoutDelay?: number;
}

interface IOptions {
    timeout?: ITimeout,
    fallbackSrc?: string,
}

type TEvent = Event | string | undefined;

export interface IProgressiveImageExtendedResult {
    sourceLoaded: string,
    isFallback: boolean,
    errorEvent: TEvent,
}

const getImage = (onSuccess: () => void, onError: OnErrorEventHandlerNonNull, src?: string) => {
    if (src && isClientSide()) {
        const img = new Image();
        img.src = src;
        img.onload = onSuccess;
        if (onError) {
            img.onerror = onError;
        }
    } else {
        onError('No src');
    }
};

// использовать только для картинок (LazyLoadImage, img, picture),
// не подходит для фоновых изображений (css: background, background-image), для них ручная проверка - useWebp
export function useProgressiveImage (
    src?: string,
    timeout?: ITimeout,
    fallbackSrc = defaultImageFallback,
): string {
    const [sourceLoaded, setSourceLoaded] = useState('');
    const { hasTimeout = false, timeoutDelay = 0 } = timeout || {};

    const onSuccess = useCallback(
        () => hasTimeout
            ? setTimeout(() => setSourceLoaded(src as string), timeoutDelay)
            : setSourceLoaded(src as string),
        [src, hasTimeout, timeoutDelay],
    );

    const onError = useCallback(() => {
        setSourceLoaded(fallbackSrc);
    }, [fallbackSrc]);

    useMemo(() => getImage(onSuccess, onError, src), [onError, onSuccess, src]);

    return sourceLoaded;
}

export function useProgressiveImageWithFallbackDetection (
    src?: string,
    options?: IOptions,
): IProgressiveImageExtendedResult {
    const {
        timeout,
        fallbackSrc = defaultImageFallback,
    } = options || {};
    const [sourceLoaded, setSourceLoaded] = useState('');
    const [isFallback, setIsFallback] = useState(false);
    const [errorEvent, setErrorEvent] = useState<TEvent>(undefined);

    const { hasTimeout = false, timeoutDelay = 0 } = timeout || {};

    const onSuccess = useCallback(
        () => hasTimeout
            ? setTimeout(() => setSourceLoaded(src as string), timeoutDelay)
            : setSourceLoaded(src as string),
        [src, hasTimeout, timeoutDelay],
    );

    const onError = useCallback((event) => {
        setSourceLoaded(fallbackSrc);
        setIsFallback(true);
        setErrorEvent(event);
    }, [fallbackSrc]);

    useMemo(() => getImage(onSuccess, onError, src), [onError, onSuccess, src]);

    return ({ sourceLoaded, isFallback, errorEvent });
}
